import {
  Link,
  Outlet,
  createFileRoute,
  notFound,
  useMatches,
  useNavigate,
} from '@tanstack/react-router';

import { useLatestBedrijf } from '@/api/queries/bedrijf';
import { useBesluitType } from '@/api/queries/besluitType';
import { Divider } from '@/components/Divider/Divider';
import { DocumentTitle } from '@/components/DocumentTitle';
import { Nav } from '@/components/Nav/Nav';
import { Navbar } from '@/components/Navbar/Navbar';
import { RoleBasedAccess } from '@/components/RoleBasedAccess';
import { eStrings } from '@/constants/strings';
import {
  SolidBuilding,
  SolidFilePen,
  SolidFiles,
  SolidHouse,
  SolidLocationDot,
  SolidScroll,
} from '@/icons/components';
import { useIsExactMatch } from '@/utils/hooks/useIsExactMatch';

import {
  BesluitFilters,
  BesluitSortingOptions,
} from './-components/BesluitFilters';
import { OntwerpFilters } from './-components/OntwerpFilters';

/**
 * Route
 */
export const Route = createFileRoute('/$bedrijfLineageId/bedrijf/_layout')({
  loader: async ({ params, context: { queryClient } }) => {
    const [latestBedrijf] = await Promise.all([
      queryClient.ensureQueryData(
        useLatestBedrijf.getOptions(params.bedrijfLineageId)
      ),
      queryClient.ensureQueryData(useBesluitType.getOptions({})),
    ]);

    if (!latestBedrijf.objects?.[0]) throw notFound();
  },
  component: BedrijfComponent,
});

/**
 * Route component
 */
function BedrijfComponent() {
  const { bedrijfLineageId } = Route.useParams();
  const navigate = useNavigate();

  const bedrijf = useLatestBedrijf(bedrijfLineageId);

  const matches = useMatches();
  const subpageMatch = matches[matches.length - 1];

  const isBesluitenPage = useIsExactMatch(
    '/$bedrijfLineageId/bedrijf/_layout/besluiten'
  );

  const isOntwerpenPage = useIsExactMatch(
    '/$bedrijfLineageId/bedrijf/_layout/ontwerpen'
  );

  if (bedrijf.status === 'error') {
    navigate({
      to: '/',
      search: {
        p: 1,
      },
    });
  }

  if (!bedrijf.data?.ID) return null;

  const pageTitle = [eStrings.LBL_DIGI_V, bedrijf.data.Naam ?? ''];
  const subpageTitle = subpageMatch?.context.getTitle?.();

  if (subpageTitle) {
    pageTitle.push(subpageTitle);
  }

  return (
    <>
      <DocumentTitle sections={pageTitle} />

      <Navbar />

      <div className="container-default grid grid-cols-3 gap-4 py-5">
        <aside className="sticky left-0 top-0 col-span-1" id="bedrijf-sidebar">
          <Nav aria-labelledby="bedrijfsmenu-header" withAccordion={false}>
            <Nav.Header as="h2" id="bedrijfsmenu-header">
              Menu
            </Nav.Header>

            <Nav.List>
              <Nav.ListItem>
                <Nav.Button id="tab-overzicht" icon={SolidHouse} asChild>
                  <Link
                    to="/$bedrijfLineageId/bedrijf/overzicht"
                    params={{ bedrijfLineageId }}
                  >
                    Overzicht
                  </Link>
                </Nav.Button>
              </Nav.ListItem>

              <Nav.ListItem>
                <Nav.Button id="tab-besluiten" icon={SolidFiles} asChild>
                  <Link
                    to="/$bedrijfLineageId/bedrijf/besluiten"
                    activeOptions={{
                      includeSearch: false,
                    }}
                    search={{ p: 1 }}
                    params={{ bedrijfLineageId }}
                  >
                    {eStrings.LBL_BESLUITEN_EN_WIJZIGINGEN}
                  </Link>
                </Nav.Button>
              </Nav.ListItem>

              <Nav.ListItem>
                <RoleBasedAccess
                  allowed={['Vergunningverlener']}
                  area={['omgevingsdienst']}
                >
                  <Nav.Button id="tab-ontwerpen" icon={SolidFilePen} asChild>
                    <Link
                      to="/$bedrijfLineageId/bedrijf/ontwerpen"
                      params={{ bedrijfLineageId }}
                      activeOptions={{
                        includeSearch: false,
                      }}
                      search={{ p: 1 }}
                    >
                      Ontwerpen
                    </Link>
                  </Nav.Button>
                </RoleBasedAccess>
              </Nav.ListItem>

              <Nav.ListItem>
                <Nav.Button
                  id="tab-historische-besluiten"
                  icon={SolidScroll}
                  asChild
                >
                  <Link
                    to="/$bedrijfLineageId/bedrijf/historische-besluiten"
                    activeOptions={{
                      includeSearch: false,
                    }}
                    params={{ bedrijfLineageId }}
                    search={{ p: 1 }}
                  >
                    Historische besluiten
                  </Link>
                </Nav.Button>
              </Nav.ListItem>
            </Nav.List>
          </Nav>

          {isBesluitenPage && (
            <div>
              <Divider className="my-5" />
              <BesluitSortingOptions />
              <Divider className="my-5" />
              <BesluitFilters />
            </div>
          )}

          {isOntwerpenPage && (
            <div>
              <Divider className="my-5" />
              <OntwerpFilters />
            </div>
          )}
        </aside>

        <main className="col-span-2">
          <div className="border-b border-borderline-light pb-5">
            <h1 className="style-h1 text-black">{bedrijf.data.Naam ?? ''}</h1>
            <p className="style-body flex items-center gap-1 text-gray-700">
              <SolidBuilding />
              {`${bedrijf.data.Straatnaam} ${bedrijf.data.Huisnummer}, ${bedrijf.data.Postcode} ${bedrijf.data.Plaatsnaam}`}

              {bedrijf.data.Locatienummer && (
                <>
                  <SolidLocationDot className="-mt-px ml-4 block" size={17} />
                  {bedrijf.data.Locatienummer}
                </>
              )}
            </p>
          </div>

          <Outlet />
        </main>
      </div>
    </>
  );
}
